import type { IErrorApiResponse, ISuccessApiResponse } from '@wyz/types';
import {
  UseMutationOptions,
  emptyQueryError,
  restrictApi,
  useMutation,
  useQuery,
  OverridableQuery,
  UseQueryOptions,
  serialize,
} from '@wyz/utils';
import { useApi, useConfig } from '~/common/context';
import { PurchaseQueryKeys } from '~/purchase/api';
import { usePurchaseContext } from '~/purchase/context';
import type {
  ICreateRedistributionServicePricingPolicyPayload,
  IRedistributionErrorResponse,
  IDuplicatePolicyItem,
  IDuplicatePolicyPayload,
  IGetOneRedistributionServicePricingPolicy,
} from '~/purchase/types';

type Options = {
  create?: UseMutationOptions<
    ISuccessApiResponse<ICreateRedistributionServicePricingPolicyPayload>,
    IRedistributionErrorResponse,
    ICreateRedistributionServicePricingPolicyPayload
  >;
  get?: UseMutationOptions<
    ISuccessApiResponse<IGetOneRedistributionServicePricingPolicy>,
    IRedistributionErrorResponse,
    IErrorApiResponse
  > & { args: { policyGroupId: string } };
  update?: UseMutationOptions<
    ISuccessApiResponse<ICreateRedistributionServicePricingPolicyPayload>,
    IRedistributionErrorResponse,
    { id: string; body: ICreateRedistributionServicePricingPolicyPayload }
  >;
  getConflicted?: OverridableQuery<
    UseQueryOptions<
      ISuccessApiResponse<Array<IDuplicatePolicyItem>>,
      IRedistributionErrorResponse
    >
  > & {
    args: {
      id: number;
      customerIds: Array<string>;
    };
  };
  duplicate?: UseMutationOptions<
    ISuccessApiResponse,
    IRedistributionErrorResponse,
    { id: string; body: IDuplicatePolicyPayload }
  >;
  remove?: UseMutationOptions<
    ISuccessApiResponse,
    IRedistributionErrorResponse,
    { policyGroupId: number }
  >;
  checkOverlap?: UseMutationOptions<
    ISuccessApiResponse,
    IRedistributionErrorResponse,
    {
      policyGroupId?: number;
      customerIds: Array<string>;
      startDate: string;
      endDate?: string | null;
    }
  >;
};

export const useManageRedistributionServicePricingPolicies = <
  TRequestedKeys extends keyof Options,
  TRequestedOptions extends Options = Pick<Options, TRequestedKeys>,
>(
  options: TRequestedOptions,
) => {
  const { tenantCode } = useConfig();
  const { callCoreApi, pricingPoliciesDomainBaseUrl } = useApi();
  const { state } = usePurchaseContext();
  const idCustomer = state.targetCustomer?.id as string;

  const createMutation = useMutation({
    mutationFn: (variables) =>
      idCustomer
        ? callCoreApi<
            ISuccessApiResponse<ICreateRedistributionServicePricingPolicyPayload>
          >(
            `${pricingPoliciesDomainBaseUrl}/tenants/${tenantCode}/redistribution-service-pricing-policies`,
            { method: 'POST', body: JSON.stringify(variables) },
          )
        : emptyQueryError(),
    keysToInvalidateOnSuccess: [
      PurchaseQueryKeys.redistributionServicePricingPolicies.base(idCustomer),
    ],
    retry: false,
    ...options.create,
  });

  const getQuery = useQuery({
    queryKey: PurchaseQueryKeys.redistributionServicePricingPolicies.get(
      idCustomer,
      options.get?.args.policyGroupId as string,
    ),
    queryFn: () =>
      idCustomer && options.get?.args
        ? callCoreApi<
            ISuccessApiResponse<IGetOneRedistributionServicePricingPolicy>
          >(
            `${pricingPoliciesDomainBaseUrl}/tenants/${tenantCode}/redistribution-service-pricing-policies/${options.get.args.policyGroupId}`,
          )
        : emptyQueryError(),
    ...options.get,
    enabled: Boolean(options.get?.args),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, body }) =>
      idCustomer
        ? callCoreApi<
            ISuccessApiResponse<ICreateRedistributionServicePricingPolicyPayload>
          >(
            `${pricingPoliciesDomainBaseUrl}/tenants/${tenantCode}/redistribution-service-pricing-policies/${id}`,
            { method: 'PUT', body: JSON.stringify(body) },
          )
        : emptyQueryError(),
    keysToInvalidateOnSuccess: [
      PurchaseQueryKeys.redistributionServicePricingPolicies.base(idCustomer),
    ],
    retry: false,
    ...options.update,
  });

  const getConflictedQuery = useQuery({
    queryKey: PurchaseQueryKeys.redistributionServicePricingPolicies.conflicted(
      idCustomer,
      options.getConflicted?.args,
    ),
    queryFn: () =>
      callCoreApi<ISuccessApiResponse<Array<IDuplicatePolicyItem>>>(
        `${pricingPoliciesDomainBaseUrl}/tenants/${tenantCode}/redistribution-service-pricing-policies/${
          options.getConflicted?.args.id
        }/conflicted?${serialize({
          customerIds: options.getConflicted?.args.customerIds,
        })}`,
      ),
    refetchOnWindowFocus: false,
    ...options.getConflicted,
    enabled:
      Boolean(options.getConflicted?.args.id) &&
      Boolean(options.getConflicted?.args.customerIds.length) &&
      Boolean(idCustomer),
  });

  const duplicateMutation = useMutation({
    mutationFn: ({ id, body }) =>
      idCustomer
        ? callCoreApi<ISuccessApiResponse>(
            `${pricingPoliciesDomainBaseUrl}/tenants/${tenantCode}/redistribution-service-pricing-policies/${id}/duplicate`,
            { method: 'POST', body: JSON.stringify(body) },
          )
        : emptyQueryError(),
    keysToInvalidateOnSuccess: [
      PurchaseQueryKeys.redistributionServicePricingPolicies.base(idCustomer),
    ],
    retry: false,
    ...options.duplicate,
  });

  const removeMutation = useMutation({
    mutationFn: (variables) =>
      idCustomer
        ? callCoreApi<ISuccessApiResponse>(
            `${pricingPoliciesDomainBaseUrl}/tenants/${tenantCode}/redistribution-service-pricing-policies/${variables.policyGroupId}`,
            { method: 'DELETE' },
          )
        : emptyQueryError(),
    keysToInvalidateOnSuccess: [
      PurchaseQueryKeys.redistributionServicePricingPolicies.base(idCustomer),
    ],
    ...options.remove,
  });
  const checkOverlapMutation = useMutation({
    mutationFn: (variables) =>
      idCustomer
        ? callCoreApi<ISuccessApiResponse>(
            `${pricingPoliciesDomainBaseUrl}/tenants/${tenantCode}/redistribution-service-pricing-policies/validations/overlap`,
            { method: 'POST', body: JSON.stringify(variables) },
          )
        : emptyQueryError(),
    ...options.checkOverlap,
  });

  return restrictApi(options, {
    create: createMutation,
    get: getQuery,
    update: updateMutation,
    getConflicted: getConflictedQuery,
    duplicate: duplicateMutation,
    remove: removeMutation,
    checkOverlap: checkOverlapMutation,
  });
};
